import CalendarViewDayOutlinedIcon from "@mui/icons-material/CalendarViewDayOutlined";
import CalendarViewMonthOutlinedIcon from "@mui/icons-material/CalendarViewMonthOutlined";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import DateRangeOutlinedIcon from "@mui/icons-material/DateRangeOutlined";
import ViewWeekOutlinedIcon from "@mui/icons-material/ViewWeekOutlined";
import { Box, Button, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { addMonths, formatDate, makeCalendarDays, makeCalendarRange } from "../../utils/calendarUtils";
import { addDays, isSameDate, overlapsRange } from "../../utils/timelineUtils";
import "./OssConnectedCalendar.css";

const modeOptions = [
  { key: "year", label: "연", icon: <DateRangeOutlinedIcon fontSize="small" /> },
  { key: "month", label: "월", icon: <CalendarViewMonthOutlinedIcon fontSize="small" /> },
  { key: "week", label: "주", icon: <ViewWeekOutlinedIcon fontSize="small" /> },
  { key: "day", label: "일", icon: <CalendarViewDayOutlinedIcon fontSize="small" /> },
];

const dayStart = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const startOfWeek = (date) => {
  const start = dayStart(date);
  start.setDate(start.getDate() - start.getDay());
  return start;
};

const endOfWeek = (date) => addDays(startOfWeek(date), 6);

const getCalendarWeeks = (days = [], mode) => {
  if (mode === "day") return [days];
  return Array.from({ length: Math.ceil(days.length / 7) }, (_, index) => days.slice(index * 7, index * 7 + 7));
};

const getRangeByMode = (mode, anchorDate) => {
  const anchor = dayStart(anchorDate);
  if (mode === "day") return { start: anchor, end: anchor, title: `${anchor.getFullYear()}년 ${anchor.getMonth() + 1}월 ${anchor.getDate()}일` };
  if (mode === "week") {
    const start = startOfWeek(anchor);
    const end = endOfWeek(anchor);
    return { start, end, title: `${start.getMonth() + 1}/${start.getDate()} - ${end.getMonth() + 1}/${end.getDate()}` };
  }
  if (mode === "year") {
    const start = new Date(anchor.getFullYear(), 0, 1);
    const end = new Date(anchor.getFullYear(), 11, 31);
    return { start, end, title: `${anchor.getFullYear()}년` };
  }
  const range = makeCalendarRange(anchor);
  return { ...range, title: `${anchor.getFullYear()}년 ${anchor.getMonth() + 1}월` };
};

const moveDate = (date, mode, direction) => {
  if (mode === "year") return new Date(date.getFullYear() + direction, date.getMonth(), 1);
  if (mode === "month") return addMonths(date, direction);
  if (mode === "week") return addDays(date, direction * 7);
  return addDays(date, direction);
};

const getSegment = (row, periodDays) => {
  const periodStart = periodDays[0];
  const periodEnd = periodDays[periodDays.length - 1];
  if (!overlapsRange(row, periodStart, periodEnd)) return null;

  const segmentStart = row.startDate > periodStart ? row.startDate : periodStart;
  const segmentEnd = row.dueDate < periodEnd ? row.dueDate : periodEnd;
  const startIndex = periodDays.findIndex((day) => isSameDate(day, segmentStart));
  const endIndex = periodDays.findIndex((day) => isSameDate(day, segmentEnd));

  return {
    startColumn: (startIndex >= 0 ? startIndex : 0) + 1,
    endColumn: (endIndex >= 0 ? endIndex : periodDays.length - 1) + 2,
    startsInRange: isSameDate(segmentStart, row.startDate),
    endsInRange: isSameDate(segmentEnd, row.dueDate),
  };
};

const getSegmentLabel = (segment, row) => {
  if (segment.startsInRange) return `착수 · ${row.orderNo}`;
  if (segment.endsInRange) return `납기 · ${row.orderNo}`;
  return row.orderNo;
};

const CalendarTooltipContent = ({ row }) => (
  <Box sx={{ minWidth: 250, maxWidth: 320 }}>
    <Typography sx={{ mb: 1, fontSize: 13.5, fontWeight: 900, lineHeight: 1.35, color: "#fff" }}>
      {row.orderNo || "-"} · {row.itemName || "품목명 없음"}
    </Typography>
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "56px minmax(0, 1fr)",
        gap: "6px 10px",
        fontSize: 12.5,
        lineHeight: 1.35,
      }}
    >
      {[
        ["거래처", row.customerName],
        ["품목", row.itemName],
        ["착수", row.startLabel],
        ["납기", row.dueLabel],
        ["여유일", row.availableMarginDays === null || row.availableMarginDays === undefined ? "-" : `${row.availableMarginDays}일`],
        ["일정", row.scheduleLabel],
        ["위험", row.riskLabel],
      ].map(([label, value]) => (
        <Box key={label} sx={{ display: "contents" }}>
          <Box component="span" sx={{ color: "#cbd5e1", fontWeight: 600 }}>
            {label}
          </Box>
          <Box component="strong" sx={{ minWidth: 0, color: "#fff", fontWeight: 800, wordBreak: "keep-all" }}>
            {value || "-"}
          </Box>
        </Box>
      ))}
    </Box>
  </Box>
);

const calendarTooltipProps = {
  describeChild: true,
  arrow: true,
  placement: "top",
  enterDelay: 120,
  componentsProps: {
    tooltip: {
      sx: {
        p: 1.5,
        borderRadius: "8px",
        bgcolor: "#111827",
        boxShadow: "0 12px 28px rgba(15, 23, 42, 0.22)",
      },
    },
    arrow: { sx: { color: "#111827" } },
  },
};

const YearOverview = ({ rows, anchorDate, onSelectMonth, onOpenOrder }) => {
  const today = dayStart(new Date());
  const months = Array.from({ length: 12 }, (_, month) => {
    const start = new Date(anchorDate.getFullYear(), month, 1);
    const end = new Date(anchorDate.getFullYear(), month + 1, 0);
    const items = rows.filter((row) => overlapsRange(row, start, end));
    const dueItems = rows.filter((row) => row.dueDate >= start && row.dueDate <= end);
    return { month, start, end, items, dueItems };
  });

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "grid",
        gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))" },
        gap: 1,
        overflow: "hidden",
      }}
    >
      {months.map((item) => {
        const isThisMonth = today.getFullYear() === anchorDate.getFullYear() && today.getMonth() === item.month;
        return (
          <Box
            key={item.month}
            component="button"
            type="button"
            onClick={() => onSelectMonth(item.start)}
            sx={{
              p: 1.25,
              border: `1px solid ${isThisMonth ? "#6c5ce7" : "#e5e7eb"}`,
              borderRadius: "8px",
              bgcolor: isThisMonth ? "#f5f3ff" : "#fff",
              textAlign: "left",
              cursor: "pointer",
              minHeight: 0,
              overflow: "hidden",
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Typography sx={{ fontSize: 15, fontWeight: 900, color: "#111827" }}>{item.month + 1}월</Typography>
              {isThisMonth && (
                <Typography sx={{ px: 0.75, py: 0.2, borderRadius: 999, bgcolor: "#6c5ce7", color: "#fff", fontSize: 11, fontWeight: 900 }}>
                  오늘
                </Typography>
              )}
            </Stack>
            <Stack direction="row" spacing={0.8} sx={{ mt: 0.8 }}>
              <Typography sx={{ fontSize: 12.3, color: "#64748b" }}>일정 {item.items.length}건</Typography>
              <Typography sx={{ fontSize: 12.3, color: "#64748b" }}>납기 {item.dueItems.length}건</Typography>
            </Stack>
            <Stack spacing={0.45} sx={{ mt: 0.9 }}>
              {item.items.slice(0, 2).map((row) => (
                <Box
                  key={`${item.month}-${row.id}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    onOpenOrder?.(row.orderId);
                  }}
                  sx={{
                    px: 0.8,
                    py: 0.45,
                    borderRadius: "6px",
                    bgcolor: row.riskBg,
                    color: row.riskTone,
                    fontSize: 11.5,
                    fontWeight: 800,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {row.orderNo} · {row.itemName}
                </Box>
              ))}
            </Stack>
          </Box>
        );
      })}
    </Box>
  );
};

export const CalendarScheduleBody = ({ rows, weeks, viewMode, anchorDate, today, onOpenOrder, onShowWeek }) => (
  <div className={`oss-connected-calendar is-${viewMode}`} role="region" aria-label="품목 일정" tabIndex={0}>
    {viewMode !== "day" && <div className="oss-calendar-weekdays">
      {["일", "월", "화", "수", "목", "금", "토"].map(label => <span key={label}>{label}</span>)}
    </div>}
    {weeks.map((days) => {
      const items = rows.map(row => ({ row, segment: getSegment(row, days) })).filter(item => item.segment);
      const visible = viewMode === "month" ? items.slice(0, 3) : items;
      const hidden = items.length - visible.length;
      return <section className="oss-calendar-period" key={formatDate(days[0])} aria-label={`${formatDate(days[0])} 일정`}>
        <div className="oss-calendar-dates" style={{ gridTemplateColumns: `repeat(${days.length}, minmax(0, 1fr))` }}>
          {days.map(date => <div key={formatDate(date)} className={[
            isSameDate(date, today) ? "is-today" : "",
            viewMode === "month" && date.getMonth() !== anchorDate.getMonth() ? "is-outside" : "",
          ].join(" ")}>
            <time dateTime={formatDate(date)}>{viewMode === "month" ? date.getDate() : `${date.getMonth() + 1}/${date.getDate()}`}</time>
            {isSameDate(date, today) && <span className="oss-calendar-today">오늘</span>}
          </div>)}
        </div>
        {viewMode === "day" ? <div className="oss-calendar-agenda">
          <div className="oss-calendar-agenda-count">일정 {items.length}건</div>
          {visible.map(({ row, segment }) => <Tooltip key={row.id} title={<CalendarTooltipContent row={row} />} {...calendarTooltipProps}>
            <button type="button" className="oss-calendar-agenda-row" onClick={() => onOpenOrder?.(row.orderId)}>
              <span className="oss-calendar-item"><strong>{row.itemName}</strong><small>{row.orderNo} · {row.customerName}</small></span>
              <span className="oss-calendar-period-label"><small>착수 → 납기</small>{row.startLabel} → {row.dueLabel}</span>
              <span className="oss-calendar-state" style={{ color: row.riskTone }}>{segment.startsInRange ? "오늘 착수" : segment.endsInRange ? "오늘 납기" : row.scheduleLabel} · {row.riskLabel}</span>
            </button>
          </Tooltip>)}
        </div> : <div className="oss-calendar-lanes">
          {visible.map(({ row, segment }) => <div className="oss-calendar-lane" key={row.id}>
            <Tooltip title={<CalendarTooltipContent row={row} />} {...calendarTooltipProps}>
              <button type="button" className="oss-calendar-event" onClick={() => onOpenOrder?.(row.orderId)}
                aria-label={`${row.orderNo} · ${row.itemName} · ${row.customerName}`}
                style={{ gridColumn: `${segment.startColumn} / ${segment.endColumn}`, '--risk-color': row.riskTone,
                  borderLeftStyle: segment.startsInRange ? "solid" : "dashed", borderRightStyle: segment.endsInRange ? "solid" : "dashed" }}>
                <span>{row.customerName} · {row.itemName}</span><small>{getSegmentLabel(segment, row)}</small>
              </button>
            </Tooltip>
          </div>)}
        </div>}
        {hidden > 0 && <button type="button" className="oss-calendar-more" onClick={() => onShowWeek(days[0])}
          aria-label={`${formatDate(days[0])} 주 일정 ${items.length}건 전체 보기`}>+{hidden}건 더보기 <span>전체 {items.length}건</span></button>}
        {!items.length && <div className="oss-calendar-empty">일정이 없습니다.</div>}
      </section>;
    })}
  </div>
);

export const OssDashboardConnectedCalendar = ({ rows = [], onOpenOrder }) => {
  const [viewMode, setViewMode] = useState("month");
  const [anchorDate, setAnchorDate] = useState(() => dayStart(new Date()));
  const today = useMemo(() => dayStart(new Date()), []);
  const range = useMemo(() => getRangeByMode(viewMode, anchorDate), [viewMode, anchorDate]);
  const periodDays = useMemo(() => {
    if (viewMode === "year") return [];
    return makeCalendarDays(range.start, range.end);
  }, [range.start, range.end, viewMode]);
  const weeks = useMemo(() => getCalendarWeeks(periodDays, viewMode), [periodDays, viewMode]);

  return (
    <Stack spacing={1.1} sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
      <Stack direction={{ xs: "column", md: "row" }} alignItems={{ xs: "stretch", md: "center" }} justifyContent="space-between" spacing={1} sx={{ flexShrink: 0 }}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          {modeOptions.map((option) => (
            <Button
              key={option.key}
              size="small"
              variant={viewMode === option.key ? "contained" : "outlined"}
              aria-pressed={viewMode === option.key}
              startIcon={option.icon}
              onClick={() => setViewMode(option.key)}
              sx={{ minWidth: 70, height: 34 }}
            >
              {option.label}
            </Button>
          ))}
        </Stack>
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.75}>
          <IconButton size="small" onClick={() => setAnchorDate((prev) => moveDate(prev, viewMode, -1))} sx={{ width: 34, height: 34, color: "#64748b" }}>
            <ChevronLeftRoundedIcon />
          </IconButton>
          <Button
            variant="text"
            onClick={() => setAnchorDate(dayStart(new Date()))}
            sx={{
              minWidth: 170,
              color: "#111827",
              fontSize: "17px !important",
              fontWeight: "900 !important",
              "&:hover": { bgcolor: "#f8fafc" },
            }}
          >
            {range.title}
          </Button>
          <IconButton size="small" onClick={() => setAnchorDate((prev) => moveDate(prev, viewMode, 1))} sx={{ width: 34, height: 34, color: "#64748b" }}>
            <ChevronRightRoundedIcon />
          </IconButton>
          <Button size="small" variant="outlined" onClick={() => setAnchorDate(dayStart(new Date()))} sx={{ height: 34 }}>
            오늘
          </Button>
        </Stack>
      </Stack>

      {viewMode === "year" ? (
        <YearOverview
          rows={rows}
          anchorDate={anchorDate}
          onOpenOrder={onOpenOrder}
          onSelectMonth={(date) => {
            setAnchorDate(date);
            setViewMode("month");
          }}
        />
      ) : (
        <CalendarScheduleBody key={`${viewMode}-${formatDate(anchorDate)}`} rows={rows} weeks={weeks} viewMode={viewMode} anchorDate={anchorDate}
          today={today} onOpenOrder={onOpenOrder} onShowWeek={(date) => {
            setAnchorDate(date);
            setViewMode("week");
          }} />
      )}
    </Stack>
  );
};
